import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { UnitSelect, rid } from "@/components/UnitSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { DeliveryNotePrint } from "@/components/DeliveryNotePrint";

export default function Returns() {
  const [tab, setTab] = useState("customer");
  const [returns, setReturns] = useState([]);
  const [armadaReturns, setArmadaReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [openCust, setOpenCust] = useState(false);
  const [openArm, setOpenArm] = useState(false);

  // Customer return
  const [cSalesId, setCSalesId] = useState("");
  const [cInv, setCInv] = useState("");
  const [cCust, setCCust] = useState("");
  const [cDest, setCDest] = useState("sales");
  const [cReason, setCReason] = useState("");
  const [cDate, setCDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [cItems, setCItems] = useState([{ _k: rid(), product_id: "", qty: 1, condition: "Baik", unit: "" }]);
  const [cFromInv, setCFromInv] = useState(null);
  const [editingRet, setEditingRet] = useState(null);

  const loadInvoice = async () => {
    if (!cInv.trim()) return;
    try {
      const inv = (await api.get(`/invoices/by-number/${encodeURIComponent(cInv.trim())}`)).data;
      setCFromInv(inv);
      setCSalesId(inv.sales_id);
      setCCust(inv.customer);
      setCItems(inv.items.map(it => ({ _k: rid(), product_id: it.product_id, qty: it.qty, condition: "Baik", unit: it.unit, max: it.qty })));
      toast.success(`Invoice ${inv.trx_no} dimuat — ${inv.items.length} barang`);
    } catch { toast.error("Invoice tidak ditemukan"); }
  };

  // Armada return
  const [aSalesId, setASalesId] = useState("");
  const [aDate, setADate] = useState(() => new Date().toISOString().slice(0, 10));
  const [aNotes, setANotes] = useState("");
  const [aItems, setAItems] = useState([{ _k: rid(), product_id: "", qty: 1, unit: "", condition: "Baik" }]);
  const [printArm, setPrintArm] = useState(null);

  const load = useCallback(async () => {
    const [a, b, c, d] = await Promise.all([api.get("/returns"), api.get("/armada-returns"), api.get("/products"), api.get("/sales-agents")]);
    setReturns(a.data); setArmadaReturns(b.data); setProducts(c.data); setSalesList(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetCustForm = () => {
    setCItems([{ _k: rid(), product_id: "", qty: 1, condition: "Baik", unit: "" }]);
    setCCust(""); setCInv(""); setCReason(""); setCSalesId(""); setCFromInv(null); setEditingRet(null); setCDest("sales");
    setCDate(new Date().toISOString().slice(0, 10));
  };

  const openEditRet = (r) => {
    setEditingRet(r);
    setCSalesId(r.sales_id); setCCust(r.customer); setCInv(r.invoice_ref || "");
    setCDest(r.destination); setCReason(r.reason || ""); setCFromInv(null);
    setCDate(r.transaction_date || String(r.date).slice(0, 10));
    setCItems(r.items.map(it => ({ _k: rid(), product_id: it.product_id, qty: it.qty, condition: it.condition || "Baik", unit: it.unit })));
    setOpenCust(true);
  };

  const deleteRet = async (r) => {
    if (!window.confirm(`Hapus retur ${r.trx_no}? Stok akan dikembalikan ke posisi semula.`)) return;
    try { await api.delete(`/returns/${r.return_id}`); toast.success("Retur dihapus — stok direversal"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const saveCust = async () => {
    try {
      const payload = { sales_id: cSalesId, customer: cCust, invoice_ref: cInv, destination: cDest, reason: cReason, date: cDate, items: cItems.filter(i => i.product_id && i.qty > 0) };
      if (editingRet) {
        await api.put(`/returns/${editingRet.return_id}`, payload);
        toast.success("Retur diperbarui");
      } else {
        await api.post("/returns", payload);
        toast.success("Retur tersimpan");
      }
      setOpenCust(false); load(); resetCustForm();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const deleteArm = async (r) => {
    if (!window.confirm(`Hapus pengembalian ${r.trx_no}? Stok akan dikembalikan ke armada ${r.sales_name}.`)) return;
    try {
      await api.delete(`/armada-returns/${r.armada_return_id}`);
      toast.success("Pengembalian dihapus — stok kembali ke armada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const saveArm = async () => {
    try {
      await api.post("/armada-returns", { sales_id: aSalesId, notes: aNotes, date: aDate, items: aItems.filter(i => i.product_id && i.qty > 0) });
      toast.success("Pengembalian gudang tersimpan"); setOpenArm(false); load();
      setAItems([{ _k: rid(), product_id: "", qty: 1, unit: "", condition: "Baik" }]); setANotes(""); setASalesId(""); setADate(new Date().toISOString().slice(0, 10));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const destBadge = { sales: "bg-indigo-100 text-indigo-800", warehouse: "bg-emerald-100 text-emerald-800", damaged: "bg-rose-100 text-rose-800" };
  const destLabel = { sales: "Ke Armada", warehouse: "Ke Gudang", damaged: "Rusak" };

  return (
    <div data-testid="page-returns" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Transaksi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Retur & Pengembalian</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="customer" data-testid="tab-customer">Retur dari Customer</TabsTrigger>
          <TabsTrigger value="armada" data-testid="tab-armada">Pengembalian Armada ke Gudang</TabsTrigger>
        </TabsList>

        <TabsContent value="customer" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openCust} onOpenChange={setOpenCust}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-return" onClick={resetCustForm}><Plus className="h-4 w-4 mr-1" />Retur Baru</Button></DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader><DialogTitle>{editingRet ? `Ubah Retur ${editingRet.trx_no}` : "Retur Penjualan"}</DialogTitle><DialogDescription className="sr-only">Form retur penjualan dari customer</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>No. Invoice Referensi</Label>
                    <div className="flex gap-2">
                      <Input value={cInv} onChange={(e) => { setCInv(e.target.value.toUpperCase()); setCFromInv(null); }} placeholder="cth: INV-20260829-0004" data-testid="input-invoice-ref" disabled={!!editingRet} />
                      {!editingRet && <Button type="button" variant="outline" onClick={loadInvoice} data-testid="btn-load-invoice">Muat</Button>}
                    </div>
                    {cFromInv && <div className="text-xs text-emerald-700 mt-1" data-testid="invoice-loaded">✓ {cFromInv.trx_no} · {cFromInv.customer} · {cFromInv.sales_name} — barang otomatis terisi, sesuaikan jumlah yang diretur</div>}
                  </div>
                  <div><Label>Sales</Label>
                    <Select value={cSalesId} onValueChange={setCSalesId} disabled={!!cFromInv || !!editingRet}><SelectTrigger data-testid="input-sales"><SelectValue placeholder="Pilih" /></SelectTrigger>
                      <SelectContent>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Customer</Label><Input value={cCust} onChange={(e) => setCCust(e.target.value)} /></div>
                  <div><Label>Tujuan Retur</Label>
                    <Select value={cDest} onValueChange={setCDest}><SelectTrigger data-testid="input-destination"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="sales">Ke Armada Sales</SelectItem><SelectItem value="warehouse">Ke Gudang</SelectItem><SelectItem value="damaged">Rusak (Tidak Dijual)</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tanggal Retur *</Label><Input type="date" value={cDate} onChange={(e) => setCDate(e.target.value)} required data-testid="input-return-date" /></div>
                  <div><Label>Alasan</Label><Input value={cReason} onChange={(e) => setCReason(e.target.value)} /></div>
                </div>
                {cItems.map((it, i) => (
                  <div key={it._k} className="grid grid-cols-[1fr_60px_80px_100px_40px] gap-2 items-center">
                    <Select value={it.product_id} onValueChange={(v) => { const n = [...cItems]; n[i].product_id = v; const p = products.find(p => p.product_id === v); if (p) n[i].unit = p.unit; setCItems(n); }}>
                      <SelectTrigger data-testid={`row-${i}-product`}><SelectValue placeholder="Barang" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <div>
                      <Input type="number" value={it.qty} onChange={(e) => { const n = [...cItems]; n[i].qty = e.target.value; setCItems(n); }} data-testid={`row-${i}-qty`} />
                      {it.max != null && <div className="text-[10px] text-slate-400 mt-0.5">maks {it.max}</div>}
                    </div>
                    <UnitSelect product={products.find(p => p.product_id === it.product_id)} value={it.unit} onChange={(v) => { const n = [...cItems]; n[i].unit = v; setCItems(n); }} testid={`row-${i}-unit`} />
                    <Select value={it.condition} onValueChange={(v) => { const n = [...cItems]; n[i].condition = v; setCItems(n); }}>
                      <SelectTrigger data-testid={`row-${i}-condition`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Baik">Baik</SelectItem><SelectItem value="Rusak">Rusak</SelectItem><SelectItem value="Tidak Layak Jual">Tidak Layak Jual</SelectItem></SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => setCItems(cItems.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setCItems([...cItems, { _k: rid(), product_id: "", qty: 1, condition: "Baik", unit: "" }])}><Plus className="h-3 w-3 mr-1" />Baris</Button>
                <DialogFooter><Button onClick={saveCust} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-return">Simpan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Sales</th><th>Customer</th><th>Tujuan</th><th className="text-right">Nilai</th><th></th></tr>
              </thead>
              <tbody>
                {returns.map(r => (
                  <tr key={r.return_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(r.date)}</td><td>{r.sales_name}</td><td>{r.customer}</td>
                    <td><Badge className={destBadge[r.destination] + " font-normal"}>{destLabel[r.destination]}</Badge></td>
                    <td className="text-right font-mono">{rupiah(r.total)}</td>
                    <td className="whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEditRet(r)} data-testid={`edit-return-${r.trx_no}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteRet(r)} data-testid={`delete-return-${r.trx_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </td></tr>
                ))}
                {returns.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Belum ada retur</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="armada" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={openArm} onOpenChange={setOpenArm}>
              <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-armada-return"><Plus className="h-4 w-4 mr-1" />Pengembalian Baru</Button></DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader><DialogTitle>Pengembalian Armada ke Gudang</DialogTitle><DialogDescription className="sr-only">Form pengembalian stok armada ke gudang</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Sales</Label>
                    <Select value={aSalesId} onValueChange={setASalesId}><SelectTrigger data-testid="input-sales"><SelectValue placeholder="Pilih sales" /></SelectTrigger>
                      <SelectContent>{salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Tanggal Pengembalian *</Label><Input type="date" value={aDate} onChange={(e) => setADate(e.target.value)} required data-testid="input-armada-return-date" /></div>
                </div>
                {aItems.map((it, i) => (
                  <div key={it._k} className="grid grid-cols-[1fr_70px_90px_130px_40px] gap-2 items-center">
                    <Select value={it.product_id} onValueChange={(v) => { const n = [...aItems]; n[i].product_id = v; const p = products.find(p => p.product_id === v); if (p) n[i].unit = p.unit; setAItems(n); }}>
                      <SelectTrigger data-testid={`row-${i}-product`}><SelectValue placeholder="Barang" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" value={it.qty} onChange={(e) => { const n = [...aItems]; n[i].qty = e.target.value; setAItems(n); }} data-testid={`row-${i}-qty`} />
                    <UnitSelect product={products.find(p => p.product_id === it.product_id)} value={it.unit} onChange={(v) => { const n = [...aItems]; n[i].unit = v; setAItems(n); }} testid={`row-${i}-unit`} />
                    <Select value={it.condition} onValueChange={(v) => { const n = [...aItems]; n[i].condition = v; setAItems(n); }}>
                      <SelectTrigger data-testid={`row-${i}-condition`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Baik">Baik</SelectItem><SelectItem value="Rusak">Rusak</SelectItem><SelectItem value="Tidak Layak Jual">Tidak Layak Jual</SelectItem></SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => setAItems(aItems.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setAItems([...aItems, { _k: rid(), product_id: "", qty: 1, unit: "", condition: "Baik" }])}><Plus className="h-3 w-3 mr-1" />Baris</Button>
                <div><Label>Keterangan</Label><Input value={aNotes} onChange={(e) => setANotes(e.target.value)} /></div>
                <DialogFooter><Button onClick={saveArm} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-armada-return">Simpan</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Sales</th><th>Ket</th><th className="text-right">Nilai</th><th></th></tr>
              </thead>
              <tbody>
                {armadaReturns.map(r => (
                  <tr key={r.armada_return_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(r.date)}</td><td>{r.sales_name}</td><td className="text-slate-500">{r.notes}</td><td className="text-right font-mono">{rupiah(r.total)}</td>
                    <td className="whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setPrintArm(r)} data-testid={`print-armada-return-${r.trx_no}`}><Printer className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteArm(r)} data-testid={`delete-armada-return-${r.trx_no}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </td></tr>
                ))}
                {armadaReturns.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Belum ada pengembalian</td></tr>}
              </tbody>
            </table>
          </Card>
          <DeliveryNotePrint doc={printArm} kind="Pengembalian ke Gudang" onClose={() => setPrintArm(null)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
